import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import { useRouteMatch } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import {
  Box,
  Button,
  LinearProgress,
  TextField,
  Typography,
} from '@material-ui/core'
import { useQuery, useMutation } from '@apollo/client'
import { useAuth } from 'lib/auth'
import { useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { PageHeader } from 'components/page-header'
import Error from 'components/error'
import { usePortal } from 'lib/portal'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import TimerIcon from 'components/material-symbol/icons/Timer'
import CheckCircleIcon from 'components/material-symbol/icons/CheckCircle'
import BlockIcon from 'components/material-symbol/icons/Block'
import DescriptionIcon from 'components/material-symbol/icons/Description'
import useStyles from './styles'
import useSettingsStyles from '../settings.styles'
import {
  SettingsChoiceRow,
  SettingsOptionCard,
  SettingsSection,
} from '../settings-components'
import SettingsSelect from '../settings-select'
import CheckIcon from 'components/material-symbol/icons/Check'
import { FormActionBar } from 'components/form-util'
import { toBoolean } from 'lib/utils/boolean'
import { GET_BOOKING_LIMIT_OPTIONS, SET_BOOKING_LIMIT } from './query'

const CANCELLATION_POLICY_MAX_LENGTH = 1000

const BookingLimitsPage = () => {
  const { path } = useRouteMatch()
  const { user } = useAuth()
  const userId = user?.coach?.id
  const [target] = usePortal()
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const [bookingLimitValue, setBookingLimitValue] = useState(null)
  const [bookingCutoffValue, setBookingCutoffValue] = useState(null)
  const [initialValues, setInitialValues] = useState(null)
  const [checked, setChecked] = useState(true)
  const [cancellationPolicy, setCancellationPolicy] = useState('')

  const [updateCoachPreferences, { loading: saving }] = useMutation(
    SET_BOOKING_LIMIT,
    {
      onCompleted: data => {
        const limitChanged = data?.updateCoachPreferences?.success
        if (!limitChanged) {
          return snackbarError(data?.updateCoachPreferences?.message)
        }
        const coach = data?.updateCoachPreferences?.coach
        if (coach) {
          setInitialValues({
            bookingLimit: coach.bookingLimit,
            bookingCutoff: coach.bookingCutoff,
            canStudentCancel: toBoolean(coach.canStudentCancel),
            cancellationPolicy: coach.cancellationPolicy ?? '',
          })
          setCancellationPolicy(coach.cancellationPolicy ?? '')
        }
        snackbarSuccess('Successfully saved booking and cancellation rules')
      },
      onError: error => {
        window.rg4js &&
          window.rg4js('send', {
            error: error,
            tags: ['set-booking-limit'],
          })
        snackbarError('Booking and cancellation rules were not saved.')
      },
    },
  )

  const handleSave = e => {
    e.preventDefault()
    updateCoachPreferences({
      variables: {
        input: {
          bookingLimit: bookingLimitValue,
          bookingCutoff: bookingCutoffValue,
          canStudentCancel: checked,
          // Only sent while cancellations are allowed; the API ignores it otherwise.
          ...(checked && { cancellationPolicy }),
        },
      },
    })
  }

  const crumbs = [
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
  ]

  const { data, loading, error } = useQuery(GET_BOOKING_LIMIT_OPTIONS, {
    variables: { id: userId },
    onCompleted: data => {
      const canStudentCancel = toBoolean(data?.coach?.canStudentCancel)
      setBookingLimitValue(data?.coach?.bookingLimit)
      setBookingCutoffValue(data?.coach?.bookingCutoff)
      setChecked(canStudentCancel)
      setCancellationPolicy(data?.coach?.cancellationPolicy ?? '')
      setInitialValues({
        bookingLimit: data?.coach?.bookingLimit,
        bookingCutoff: data?.coach?.bookingCutoff,
        canStudentCancel,
        cancellationPolicy: data?.coach?.cancellationPolicy ?? '',
      })
    },
  })

  const isDirty = useMemo(() => {
    if (!initialValues) return false

    return (
      bookingLimitValue !== initialValues.bookingLimit ||
      bookingCutoffValue !== initialValues.bookingCutoff ||
      checked !== initialValues.canStudentCancel ||
      (checked && cancellationPolicy !== initialValues.cancellationPolicy)
    )
  }, [
    bookingLimitValue,
    bookingCutoffValue,
    checked,
    cancellationPolicy,
    initialValues,
  ])

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  return (
    <>
      <PageHeader
        title="Booking & Cancellation Rules"
        crumbs={crumbs}
        actions={target}
      />
      {isDirty && (
        <FormActionBar variant="toolbar" disableCancel={saving} includeCancel>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={handleSave}
            startIcon={<CheckIcon fontSize="small" />}
            disabled={saving}
          >
            Save
          </Button>
        </FormActionBar>
      )}
      <Box
        className={clsx(
          settingsStyles.contentContainer,
          isDirty && settingsStyles.contentContainerWithStickySave,
        )}
      >
        <SettingsSection eyebrow="Booking window" classes={settingsStyles}>
          <SettingsOptionCard
            icon={<CalendarTodayIcon />}
            title="How far in advance can students book?"
            description='Applied to all individual offerings. For example, "1 month" lets a student book up to 30 days out.'
            classes={settingsStyles}
          >
            <SettingsSelect
              label="How far in advance students can book"
              value={bookingLimitValue ?? ''}
              options={data?.bookingLimitOptions ?? []}
              onChange={setBookingLimitValue}
            />
          </SettingsOptionCard>
        </SettingsSection>

        <SettingsSection eyebrow="Booking cutoff" classes={settingsStyles}>
          <SettingsOptionCard
            icon={<TimerIcon />}
            title="Minimum lead time before a session"
            description='For example, "4 hours" lets a student book up until 4 hours before your next available session.'
            classes={settingsStyles}
          >
            <SettingsSelect
              label="Minimum lead time before a session"
              value={bookingCutoffValue ?? ''}
              options={data?.bookingCutoffOptions ?? []}
              onChange={setBookingCutoffValue}
            />
          </SettingsOptionCard>
        </SettingsSection>

        <SettingsSection
          eyebrow="Student cancellations"
          classes={settingsStyles}
        >
          <SettingsChoiceRow
            icon={<CheckCircleIcon />}
            title="Allow Cancellations"
            badge="Default"
            description="Students can cancel their individual bookings with you."
            checked={checked}
            onSelect={() => setChecked(true)}
            classes={settingsStyles}
          />
          <SettingsChoiceRow
            icon={<BlockIcon />}
            title="Prevent Cancellations"
            description="Students cannot cancel individual bookings with you."
            checked={!checked}
            onSelect={() => setChecked(false)}
            classes={settingsStyles}
          />
        </SettingsSection>

        {checked && (
          <SettingsSection
            eyebrow="Cancellation policy"
            classes={settingsStyles}
          >
            <SettingsOptionCard
              icon={<DescriptionIcon />}
              title="What should students know before they book?"
              description="Set your cancellation policy once, and students will see it when booking and in their confirmation email. Refunds are not processed automatically, so you'll receive a reminder to issue a refund when a student cancels."
              classes={settingsStyles}
            >
              <TextField
                value={cancellationPolicy}
                onChange={e => setCancellationPolicy(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                className={classes.policyField}
                placeholder="Example: Cancel at least 24 hours before your session for a full refund. Later cancellations are not refunded."
                inputProps={{
                  maxLength: CANCELLATION_POLICY_MAX_LENGTH,
                  'aria-label': 'Cancellation policy',
                  'data-testid': 'cancellation-policy-input',
                }}
              />
              <Box className={classes.policyFooter}>
                <Typography component="p" className={classes.policyCount}>
                  {cancellationPolicy.length} / {CANCELLATION_POLICY_MAX_LENGTH}
                </Typography>
              </Box>
            </SettingsOptionCard>
          </SettingsSection>
        )}

        <Typography component="p" className={classes.footerNote}>
          Rules apply to all individual offerings on your profile.
        </Typography>
      </Box>
    </>
  )
}

export default BookingLimitsPage
